import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { Textarea } from "@shared/components/ui/textarea";
import { Switch } from "@shared/components/ui/switch";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@shared/components/ui/tabs";
import useTitle from "@shared/hooks/useTitle";
import {
  Mail,
  MessageSquare,
  DollarSign,
  Truck,
  Shield,
  Code,
  Save,
} from "lucide-react";
import ThreeDotLoader from "@shared/components/ThreeDotLoader";
import { toast } from "sonner";
import { ADMIN_QUERY_KEYS } from "../constants";
import { adminSettingsService } from "../services/settings-service";

export default function SystemSettingsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  useTitle(t("admin.settings.title"));

  const [activeTab, setActiveTab] = useState("general");

  // Fetch settings
  const { data, isLoading } = useQuery({
    queryKey: [ADMIN_QUERY_KEYS.SYSTEM_SETTINGS],
    queryFn: () => adminSettingsService.getSettings(),
    staleTime: 5 * 60 * 1000,
  });

  // Update settings mutation
  const updateMutation = useMutation({
    mutationFn: (data) => adminSettingsService.updateSettings(data),
    onSuccess: () => {
      toast.success(t("admin.settings.updateSuccess"));
      queryClient.invalidateQueries([ADMIN_QUERY_KEYS.SYSTEM_SETTINGS]);
    },
    onError: () => {
      toast.error(t("admin.settings.updateFailed"));
    },
  });

  const settings = data?.data || {};

  const handleSave = (category, values) => {
    updateMutation.mutate({
      category,
      settings: values,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">{t("admin.settings.title")}</h2>
          <p className="text-muted-foreground">
            {t("admin.settings.description")}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <ThreeDotLoader />
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="general">
              <Shield className="h-4 w-4 mr-2" />
              {t("admin.settings.general")}
            </TabsTrigger>
            <TabsTrigger value="email">
              <Mail className="h-4 w-4 mr-2" />
              {t("admin.settings.email")}
            </TabsTrigger>
            <TabsTrigger value="sms">
              <MessageSquare className="h-4 w-4 mr-2" />
              {t("admin.settings.sms")}
            </TabsTrigger>
            <TabsTrigger value="payment">
              <DollarSign className="h-4 w-4 mr-2" />
              {t("admin.settings.payment")}
            </TabsTrigger>
            <TabsTrigger value="shipping">
              <Truck className="h-4 w-4 mr-2" />
              {t("admin.settings.shipping")}
            </TabsTrigger>
            <TabsTrigger value="security">
              <Shield className="h-4 w-4 mr-2" />
              {t("admin.settings.security")}
            </TabsTrigger>
            <TabsTrigger value="api">
              <Code className="h-4 w-4 mr-2" />
              {t("admin.settings.api")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-4">
            <GeneralSettings settings={settings.general} onSave={handleSave} />
          </TabsContent>

          <TabsContent value="email" className="space-y-4">
            <EmailSettings settings={settings.email} onSave={handleSave} />
          </TabsContent>

          <TabsContent value="sms" className="space-y-4">
            <SMSSettings settings={settings.sms} onSave={handleSave} />
          </TabsContent>

          <TabsContent value="payment" className="space-y-4">
            <PaymentSettings settings={settings.payment} onSave={handleSave} />
          </TabsContent>

          <TabsContent value="shipping" className="space-y-4">
            <ShippingSettings
              settings={settings.shipping}
              onSave={handleSave}
            />
          </TabsContent>

          <TabsContent value="security" className="space-y-4">
            <SecuritySettings
              settings={settings.security}
              onSave={handleSave}
            />
          </TabsContent>

          <TabsContent value="api" className="space-y-4">
            <APISettings settings={settings.api} onSave={handleSave} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

// Settings Components
function GeneralSettings({ settings = {}, onSave }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    siteName: settings.siteName || "",
    siteDescription: settings.siteDescription || "",
    supportEmail: settings.supportEmail || "",
    supportPhone: settings.supportPhone || "",
    maintenanceMode: settings.maintenanceMode || false,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("admin.settings.generalSettings")}</CardTitle>
        <CardDescription>
          {t("admin.settings.generalSettingsDesc")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="siteName">{t("admin.settings.siteName")}</Label>
          <Input
            id="siteName"
            value={formData.siteName}
            onChange={(e) =>
              setFormData({ ...formData, siteName: e.target.value })
            }
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="siteDescription">
            {t("admin.settings.siteDescription")}
          </Label>
          <Textarea
            id="siteDescription"
            value={formData.siteDescription}
            onChange={(e) =>
              setFormData({ ...formData, siteDescription: e.target.value })
            }
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="supportEmail">
            {t("admin.settings.supportEmail")}
          </Label>
          <Input
            id="supportEmail"
            type="email"
            value={formData.supportEmail}
            onChange={(e) =>
              setFormData({ ...formData, supportEmail: e.target.value })
            }
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="supportPhone">
            {t("admin.settings.supportPhone")}
          </Label>
          <Input
            id="supportPhone"
            value={formData.supportPhone}
            onChange={(e) =>
              setFormData({ ...formData, supportPhone: e.target.value })
            }
          />
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="maintenanceMode"
            checked={formData.maintenanceMode}
            onCheckedChange={(checked) =>
              setFormData({ ...formData, maintenanceMode: checked })
            }
          />
          <Label htmlFor="maintenanceMode">
            {t("admin.settings.maintenanceMode")}
          </Label>
        </div>

        <Button onClick={() => onSave("general", formData)} className="w-full">
          <Save className="h-4 w-4 mr-2" />
          {t("admin.settings.saveChanges")}
        </Button>
      </CardContent>
    </Card>
  );
}

function EmailSettings({ settings = {}, onSave }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    smtpHost: settings.smtpHost || "",
    smtpPort: settings.smtpPort || "",
    smtpUser: settings.smtpUser || "",
    smtpPassword: settings.smtpPassword || "",
    fromEmail: settings.fromEmail || "",
    fromName: settings.fromName || "",
    enabled: settings.enabled || false,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("admin.settings.emailSettings")}</CardTitle>
        <CardDescription>
          {t("admin.settings.emailSettingsDesc")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center space-x-2">
          <Switch
            id="emailEnabled"
            checked={formData.enabled}
            onCheckedChange={(checked) =>
              setFormData({ ...formData, enabled: checked })
            }
          />
          <Label htmlFor="emailEnabled">
            {t("admin.settings.enableEmailService")}
          </Label>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="smtpHost">{t("admin.settings.smtpHost")}</Label>
            <Input
              id="smtpHost"
              value={formData.smtpHost}
              onChange={(e) =>
                setFormData({ ...formData, smtpHost: e.target.value })
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="smtpPort">{t("admin.settings.smtpPort")}</Label>
            <Input
              id="smtpPort"
              type="number"
              value={formData.smtpPort}
              onChange={(e) =>
                setFormData({ ...formData, smtpPort: e.target.value })
              }
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="smtpUser">{t("admin.settings.smtpUser")}</Label>
          <Input
            id="smtpUser"
            value={formData.smtpUser}
            onChange={(e) =>
              setFormData({ ...formData, smtpUser: e.target.value })
            }
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="smtpPassword">
            {t("admin.settings.smtpPassword")}
          </Label>
          <Input
            id="smtpPassword"
            type="password"
            value={formData.smtpPassword}
            onChange={(e) =>
              setFormData({ ...formData, smtpPassword: e.target.value })
            }
          />
        </div>

        <Button onClick={() => onSave("email", formData)} className="w-full">
          <Save className="h-4 w-4 mr-2" />
          {t("admin.settings.saveChanges")}
        </Button>
      </CardContent>
    </Card>
  );
}

function SMSSettings({ settings = {}, onSave }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    provider: settings.provider || "",
    apiKey: settings.apiKey || "",
    senderId: settings.senderId || "",
    enabled: settings.enabled || false,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("admin.settings.smsSettings")}</CardTitle>
        <CardDescription>{t("admin.settings.smsSettingsDesc")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center space-x-2">
          <Switch
            id="smsEnabled"
            checked={formData.enabled}
            onCheckedChange={(checked) =>
              setFormData({ ...formData, enabled: checked })
            }
          />
          <Label htmlFor="smsEnabled">
            {t("admin.settings.enableSMSService")}
          </Label>
        </div>

        <div className="space-y-2">
          <Label htmlFor="provider">{t("admin.settings.smsProvider")}</Label>
          <Input
            id="provider"
            value={formData.provider}
            onChange={(e) =>
              setFormData({ ...formData, provider: e.target.value })
            }
            placeholder="Twilio, Nexmo, etc."
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="apiKey">{t("admin.settings.apiKey")}</Label>
          <Input
            id="apiKey"
            type="password"
            value={formData.apiKey}
            onChange={(e) =>
              setFormData({ ...formData, apiKey: e.target.value })
            }
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="senderId">{t("admin.settings.senderId")}</Label>
          <Input
            id="senderId"
            value={formData.senderId}
            onChange={(e) =>
              setFormData({ ...formData, senderId: e.target.value })
            }
          />
        </div>

        <Button onClick={() => onSave("sms", formData)} className="w-full">
          <Save className="h-4 w-4 mr-2" />
          {t("admin.settings.saveChanges")}
        </Button>
      </CardContent>
    </Card>
  );
}

function PaymentSettings({ onSave }) {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("admin.settings.paymentSettings")}</CardTitle>
        <CardDescription>
          {t("admin.settings.paymentSettingsDesc")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t("admin.settings.paymentPlaceholder")}
        </p>
        <Button onClick={() => onSave("payment", {})} className="w-full">
          <Save className="h-4 w-4 mr-2" />
          {t("admin.settings.saveChanges")}
        </Button>
      </CardContent>
    </Card>
  );
}

function ShippingSettings({ onSave }) {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("admin.settings.shippingSettings")}</CardTitle>
        <CardDescription>
          {t("admin.settings.shippingSettingsDesc")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t("admin.settings.shippingPlaceholder")}
        </p>
        <Button onClick={() => onSave("shipping", {})} className="w-full">
          <Save className="h-4 w-4 mr-2" />
          {t("admin.settings.saveChanges")}
        </Button>
      </CardContent>
    </Card>
  );
}

function SecuritySettings({ settings = {}, onSave }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    twoFactorAuth: settings.twoFactorAuth || false,
    sessionTimeout: settings.sessionTimeout || 30,
    maxLoginAttempts: settings.maxLoginAttempts || 5,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("admin.settings.securitySettings")}</CardTitle>
        <CardDescription>
          {t("admin.settings.securitySettingsDesc")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center space-x-2">
          <Switch
            id="twoFactorAuth"
            checked={formData.twoFactorAuth}
            onCheckedChange={(checked) =>
              setFormData({ ...formData, twoFactorAuth: checked })
            }
          />
          <Label htmlFor="twoFactorAuth">{t("admin.settings.enable2FA")}</Label>
        </div>

        <div className="space-y-2">
          <Label htmlFor="sessionTimeout">
            {t("admin.settings.sessionTimeout")}
          </Label>
          <Input
            id="sessionTimeout"
            type="number"
            value={formData.sessionTimeout}
            onChange={(e) =>
              setFormData({ ...formData, sessionTimeout: e.target.value })
            }
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="maxLoginAttempts">
            {t("admin.settings.maxLoginAttempts")}
          </Label>
          <Input
            id="maxLoginAttempts"
            type="number"
            value={formData.maxLoginAttempts}
            onChange={(e) =>
              setFormData({ ...formData, maxLoginAttempts: e.target.value })
            }
          />
        </div>

        <Button onClick={() => onSave("security", formData)} className="w-full">
          <Save className="h-4 w-4 mr-2" />
          {t("admin.settings.saveChanges")}
        </Button>
      </CardContent>
    </Card>
  );
}

function APISettings({ onSave }) {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("admin.settings.apiSettings")}</CardTitle>
        <CardDescription>{t("admin.settings.apiSettingsDesc")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t("admin.settings.apiPlaceholder")}
        </p>
        <Button onClick={() => onSave("api", {})} className="w-full">
          <Save className="h-4 w-4 mr-2" />
          {t("admin.settings.saveChanges")}
        </Button>
      </CardContent>
    </Card>
  );
}
